import React, { useState } from 'react';
import { Checkbox } from 'tea-component';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import BubbleCommon, {
  keyMetricsShowEventTips,
  type TipsMapType,
} from '@op/components/BubbleCommon';
import OpinionEventSetting from '../OpinionEventSetting';
import { isOpinionEventDateRangeExceeded } from '../hooks/utils/isOpinionEventDateRangeExceeded';
import type {
  IOpinionChartEventData,
  IOpinionChartEventSetting,
  IChangeOpinionChartEventSettingOptions,
} from '../hooks/types';
import { OPINION_BAR_AND_LINE_CHART_ROOT_CLASS } from '../constants';
import './index.scss';

const eventDateRangeDisabledTips: TipsMapType = {
  en: (
    <>
      Events are available only for date ranges within 3 years.
      <br />
      Please adjust the date range to view events.
    </>
  ),
  zh: (
    <>
      事件仅支持3年以内的时间范围。
      <br />
      请调整时间筛选器后查看。
    </>
  ),
};

export interface IOpinionEventSettingToolbarProps {
  visible?: boolean;
  eventData: IOpinionChartEventData;
  chartEventSetting: IOpinionChartEventSetting;
  onEventSettingChange: (
    setting: IOpinionChartEventSetting,
    options?: IChangeOpinionChartEventSettingOptions,
  ) => void;
  xAxisData?: string[];
  dateType?: string;
  startTime?: string;
  endTime?: string;
}

const OpinionEventSettingToolbar: React.FC<IOpinionEventSettingToolbarProps> = ({
  visible = true,
  eventData,
  chartEventSetting,
  onEventSettingChange,
  xAxisData = [],
  dateType,
  startTime,
  endTime,
}) => {
  const { formatMessage } = useIntl();
  const [settingVisible, setSettingVisible] = useState(false);
  const disabled = isOpinionEventDateRangeExceeded(startTime, endTime);
  const showEventsChecked =
    !disabled &&
    (chartEventSetting.showEventAxis || chartEventSetting.showEventBubbles);

  if (!visible) {
    return null;
  }

  const handleSettingConfirm = (data: IOpinionChartEventSetting) => {
    setSettingVisible(false);
    onEventSettingChange(data, { persist: true });
  };

  const settingInner = (
    <div className="dbo-events-setting-inner">
      <Checkbox
        disabled={disabled}
        value={showEventsChecked}
        onChange={value => {
          onEventSettingChange(
            {
              ...chartEventSetting,
              showEventAxis: value,
              showEventBubbles: value,
            },
            { persist: true },
          );
        }}
      >
        {disabled ? (
          <span>{formatMessage({ id: 'Show Events' })}</span>
        ) : (
          <BubbleCommon showIcon={false} tipsMap={keyMetricsShowEventTips}>
            <span>{formatMessage({ id: 'Show Events' })}</span>
          </BubbleCommon>
        )}
      </Checkbox>
      <span
        className="dbo-events-setting-icon"
        onClick={() => {
          if (!disabled) {
            setSettingVisible(true);
          }
        }}
      >
        <SvgIcon type="jichushezhi" />
      </span>
    </div>
  );

  return (
    <>
      <div
        className={`${OPINION_BAR_AND_LINE_CHART_ROOT_CLASS} dbi-opinion-event-setting dbo-events-setting${
          disabled ? ' dbo-events-setting--disabled' : ''
        }`}
      >
        {disabled ? (
          <BubbleCommon showIcon={false} tipsMap={eventDateRangeDisabledTips}>
            {settingInner}
          </BubbleCommon>
        ) : (
          settingInner
        )}
      </div>
      {settingVisible ? (
        <OpinionEventSetting
          data={eventData.eventList}
          visible={settingVisible}
          timeFormat={eventData.format}
          confirm={handleSettingConfirm}
          close={() => setSettingVisible(false)}
          xAxisData={xAxisData}
          dateType={dateType}
          showEventAxisValue={chartEventSetting.showEventAxis}
          showEventBubblesValue={chartEventSetting.showEventBubbles}
          bubbleEvent={chartEventSetting.bubbleEvent}
        />
      ) : null}
    </>
  );
};

export default OpinionEventSettingToolbar;
